import React, { useState, useRef, useMemo, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useInvoiceColumns, useInvoiceFormFields } from './hooks';
import { PaymentFormModal } from '@src/pages/payments/components';
import { useInvalidate, useList } from '@refinedev/core';
import {
  PrinterOutlined,
  CopyOutlined,
  ExperimentOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { InvoicePrintComponent } from './components';
import { SalesPrintComponent } from '@src/pages/medical/sales/components/SalesPrintComponent';
import { SalesFormFields } from '@src/components/shared/forms/SalesFormFields';
import { useSearchParams } from 'react-router';
import { Spin, Tag, message } from 'antd';
import { formatNumber } from '@src/utils/numberUtils';
import { FileTextOutlined, ShoppingCartOutlined } from '@ant-design/icons';

interface InvoicesTabProps {
  patientId: string | number;
  patient?: any;
}

interface PrintRef {
  print: () => void;
}

export const InvoicesTab: React.FC<InvoicesTabProps> = ({
  patientId,
  patient,
}) => {
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentInitialValues, setPaymentInitialValues] = useState<any>({});
  const [selectedRecordForPrint, setSelectedRecordForPrint] =
    useState<any>(null);
  const [currentResource, setCurrentResource] = useState<
    'clinic-invoices' | 'sales'
  >('clinic-invoices');
  const [copyInvoiceData, setCopyInvoiceData] = useState<any>(null);
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);
  const [copyId, setCopyId] = useState<number>(0);
  const [formInstanceKey, setFormInstanceKey] = useState<string>('');
  const printRef = useRef<PrintRef>(null);
  const invalidate = useInvalidate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Reset copy state when patient changes
  useEffect(() => {
    setCopyInvoiceData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');
  }, [patientId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setCopyInvoiceData(null);
      setAutoOpenCreate(false);
      setCopyId(0);
      setFormInstanceKey('');
    };
  }, []);

  // Clear copy data when form is closed without creating
  useEffect(() => {
    if (copyInvoiceData && !autoOpenCreate) {
      const timer = setTimeout(() => {
        setCopyInvoiceData(null);
        setCopyId(0);
        setFormInstanceKey('');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [copyInvoiceData, autoOpenCreate]);

  // Fetch invoices data - without pagination to get all data
  const { data: invoicesData, isLoading: invoicesLoading } = useList({
    resource: 'clinic-invoices',
    filters: [
      {
        field: 'patient_id',
        operator: 'eq',
        value: patientId,
      },
    ],
    pagination: {
      mode: 'off', // Disable pagination to fetch all data
    },
  });

  // Fetch sales data - without pagination to get all data
  const { data: salesData, isLoading: salesLoading } = useList({
    resource: 'sales',
    filters: [
      {
        field: 'patient_id',
        operator: 'eq',
        value: patientId,
      },
    ],
    pagination: {
      mode: 'off', // Disable pagination to fetch all data
    },
  });

  // Check if both datasets are loaded
  const isDataLoading = invoicesLoading || salesLoading;
  const hasInvoicesData = invoicesData?.data && invoicesData.data.length > 0;
  const hasSalesData = salesData?.data && salesData.data.length > 0;

  // Combine and transform data
  const combinedData = useMemo(() => {
    // Only process data if both datasets are loaded
    if (isDataLoading) {
      return [];
    }

    const invoices = (invoicesData?.data || []).map((invoice: any) => ({
      ...invoice,
      type: 'invoice',
      id: `${invoice.id}`,
    }));

    const sales = (salesData?.data || []).map((sale: any) => ({
      ...sale,
      type: 'sale',
      id: `${sale.id}`,
    }));

    // Sort by created_at (newest first)
    const sorted = [...invoices, ...sales].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB.getTime() - dateA.getTime();
    });

    return sorted;
  }, [invoicesData?.data, salesData?.data, isDataLoading]);

  // Calculate totals from combined data
  const totals = useMemo(() => {
    const totalAmount = combinedData.reduce((sum, record) => {
      const amount = record.total_amount || record.amount || 0;
      return sum + Number(amount);
    }, 0);

    const totalInvoiceDueAmount = combinedData.reduce((sum, record) => {
      if (record.type === 'invoice') {
        const dueAmount = record.due_amount || 0;
        return sum + Number(dueAmount);
      }
      return sum;
    }, 0);

    const totalInvoiceClinicDueAmount = combinedData.reduce((sum, record) => {
      if (record.type === 'invoice') {
        const dueAmount = record.clinic_due_amount || 0;
        return sum + Number(dueAmount);
      }
      return sum;
    }, 0);

    const totalSaleDueAmount = combinedData.reduce((sum, record) => {
      if (record.type === 'sale') {
        const dueAmount = record.due_amount || 0;
        return sum + Number(dueAmount);
      }
      return sum;
    }, 0);

    const totalSaleClinicDueAmount = combinedData.reduce((sum, record) => {
      if (record.type === 'sale') {
        const dueAmount = record.clinic_due_amount || 0;
        return sum + Number(dueAmount);
      }
      return sum;
    }, 0);

    const totalClinicDueAmount =
      totalInvoiceClinicDueAmount + totalSaleClinicDueAmount;

    const totalDueAmount = combinedData.reduce((sum, record) => {
      const dueAmount = record.due_amount || 0;
      return sum + Number(dueAmount);
    }, 0);

    return {
      totalAmount,
      totalDueAmount,
      totalInvoiceDueAmount,
      totalSaleDueAmount,
      totalInvoiceClinicDueAmount,
      totalSaleClinicDueAmount,
      totalClinicDueAmount,
    };
  }, [combinedData]);

  // Unified payment handler for both invoices and sales
  const handlePayment = (transactionData: {
    transaction_type: 'invoice' | 'sell';
    invoice_id: number;
    amount: number;
    patient_id: string | number;
    customer_name?: string;
    type: string;
  }) => {
    setPaymentInitialValues({
      type: transactionData.type,
      reference_id: transactionData.invoice_id,
      transaction_type: 'CREDIT',
      amount: transactionData.amount,
    });
    setPaymentModalVisible(true);
  };

  // Separate print handlers for invoices and sales
  const handleInvoicePrint = (record: any) => {
    setSelectedRecordForPrint(record);
    setTimeout(() => {
      printRef.current?.print();
    }, 100);
  };

  const handleSalePrint = (record: any) => {
    setSelectedRecordForPrint(record);
    setTimeout(() => {
      printRef.current?.print();
    }, 100);
  };

  const handleCreateSuccess = (data: any) => {
    setCopyInvoiceData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');
    if (data?.type === 'sale') {
      invalidate({
        resource: 'sales',
        invalidates: ['list'],
      });
    } else {
      invalidate({
        resource: 'clinic-invoices',
        invalidates: ['list'],
      });
    }
  };

  const handleCreateDrawerClose = () => {
    setCopyInvoiceData(null);
    setAutoOpenCreate(false);
    setCopyId(0);
    setFormInstanceKey('');
  };

  const handleReload = () => {
    invalidate({
      resource: 'clinic-invoices',
      invalidates: ['list'],
    });
    invalidate({
      resource: 'sales',
      invalidates: ['list'],
    });
  };

  const handleClear = () => {
    // Clear any filters or search
    setCurrentResource('clinic-invoices');
  };

  const handleCreateTreatment = (invoice: any) => {
    const invoiceItems = invoice.clinic_invoice_items || [];
    const names = invoiceItems
      .map(
        (item: any) =>
          (item.item_name || item.offering?.name || '').trim()
      )
      .filter(Boolean);
    const treatmentNames = [...new Set(names)];

    // Prepare initial values for treatment form
    const treatmentInitialValues = {
      treatment_name: treatmentNames,
      skin_type: 'Type III',
      therapist_type: 'SKIN',
      under_observation_doctor_user_id: invoice.doctor_id,
      therapist_id: invoice.therapist_id,
    };

    // Navigate to treatments tab with initial values in URL params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', 'treatments');
    newSearchParams.set('autoOpenTreatmentCreate', 'true');
    newSearchParams.set(
      'treatmentInitialValues',
      encodeURIComponent(JSON.stringify(treatmentInitialValues))
    );
    setSearchParams(newSearchParams);
  };

  const onCopyClick = (record: any) => {
    if (!record || record?.type !== 'invoice') {
      message.warning('Only clinic invoices can be copied.');
      return;
    }
    if (copyInvoiceData) {
      message.warning(
        'Please complete or cancel the current copy operation first.'
      );
      return;
    }
    try {
      const newCopyId = copyId + 1;
      const timestamp = Date.now();
      const uniqueFormKey = `form_${timestamp}_${newCopyId}_${Math.random().toString(36).substr(2, 9)}`;
      setCopyId(newCopyId);
      setFormInstanceKey(uniqueFormKey);

      const items = record.clinic_invoice_items || [];
      const totalAmount = formatNumber(Number(record.total_amount) || 0);
      const overallDiscountAmount = formatNumber(
        Number(record.overall_discount_amount) || 0
      );
      const gst = formatNumber(Number(record.gst) || 0);
      const finalAmount = formatNumber(Number(record.final_amount) || 0);
      const billAmount = formatNumber(Number(record.bill_amount) || 0);
      // Copied invoice is new; no payments yet, so due = clinic_due = bill
      const dueAmount = billAmount;
      const clinicDueAmount = billAmount;
      const overallDiscountRate =
        totalAmount > 0
          ? formatNumber((overallDiscountAmount * 100) / totalAmount)
          : 0;

      const copyData = {
        copyId: newCopyId,
        timestamp,
        formInstanceKey: uniqueFormKey,
        patient_id: patientId,
        branch_name: record.branch_name || 'Main',
        doctor_id: record.doctor_id ?? record.doctor?.id ?? undefined,
        therapist_id: record.therapist_id ?? record.therapist?.id ?? undefined,
        counsellor_id: record.counsellor_id ?? record.counsellor?.id ?? undefined,
        category: record.category ?? undefined,
        remark: record.remark ?? '',
        total_amount: totalAmount,
        overall_discount_rate: record.overall_discount_rate ?? overallDiscountRate,
        overall_discount_amount: overallDiscountAmount,
        gst,
        final_amount: finalAmount,
        bill_amount: billAmount,
        due_amount: dueAmount,
        clinic_due_amount: clinicDueAmount,
        clinic_invoice_items: items.map((item: any, index: number) => {
          const offering = item.offering;
          const offeringId = item.offering_id ?? offering?.id;
          return {
            id: `copied_item_${index}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
            offering_id: offeringId,
            offering: offering
              ? {
                  id: offering.id,
                  name: offering.name,
                  description: offering.description,
                  price: parseFloat(offering.price) ?? 0,
                  gst_rate: offering.gst_rate,
                  total_sessions: offering.total_sessions,
                  category: offering.category,
                  duration_in_mins: offering.duration_in_mins,
                }
              : undefined,
            item_name: item.item_name ?? offering?.name ?? '',
            item_description: item.item_description ?? offering?.description ?? '',
            unit_price: parseFloat(item.unit_price) ?? 0,
            quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
            discount: parseFloat(item.discount) ?? 0,
            gst_rate: parseFloat(item.gst_rate) ?? 0,
            total_sessions: item.total_sessions ?? offering?.total_sessions ?? 0,
            session_number: parseFloat(item.session_number) ?? 0,
            final_price: parseFloat(item.final_price) ?? 0,
          };
        }),
      };

      setCopyInvoiceData(copyData);
      setAutoOpenCreate(true);
      message.success('Clinic invoice copied. Create form will open.');
    } catch (error) {
      console.error('Error copying clinic invoice:', error);
      message.error('Failed to copy invoice. Please try again.');
    }
  };

  const invoiceColumns = useInvoiceColumns({
    onCreateTransaction: handlePayment,
  });
  const invoiceFormFields = useInvoiceFormFields(patientId);
  const consultationInvoiceFormFields = useInvoiceFormFields(patientId, {
    consultationOfferingsOnly: true,
  });
  const salesFormFields = SalesFormFields();

  // Unique form fields for copy (form isolation, same as prescriptions)
  const uniqueInvoiceFormFields = useMemo(() => {
    if (!copyInvoiceData || !formInstanceKey) return invoiceFormFields;
    return invoiceFormFields.map(field => ({
      ...field,
      _uniqueId: `${field.name}_${formInstanceKey}`,
      _copyOperationId: copyInvoiceData.copyId,
    }));
  }, [invoiceFormFields, copyInvoiceData, formInstanceKey]);

  // Custom actions for the table
  const customActions = [
    {
      key: 'copy',
      icon: <CopyOutlined />,
      label: '',
      onClick: onCopyClick,
      type: 'default' as const,
      tooltip: 'Copy Clinic Invoice',
      shouldShow: (record: any) => record?.type === 'invoice',
    },
    {
      key: 'print',
      icon: <PrinterOutlined />,
      label: '',
      onClick: (record: any) => {
        if (record.type === 'invoice') {
          handleInvoicePrint(record);
        } else {
          handleSalePrint(record);
        }
      },
      type: 'default' as const,
      tooltip: 'Print',
    },
    {
      key: 'treatment',
      icon: <ExperimentOutlined />,
      label: '',
      onClick: (record: any) => {
        if (record.type === 'invoice') {
          handleCreateTreatment(record);
        }
      },
      type: 'default' as const,
      tooltip: 'Create Treatment from Invoice',
      shouldShow: (record: any) => record?.type === 'invoice',
    },
  ];

  // Get the appropriate form fields based on current resource
  const getFormFields = () => {
    return currentResource === 'clinic-invoices'
      ? invoiceFormFields
      : salesFormFields;
  };

  // Get the appropriate title and submit text
  const getFormConfig = () => {
    if (currentResource === 'clinic-invoices') {
      return {
        title: 'Create Invoice',
        submitText: 'Create Invoice',
      };
    } else {
      return {
        title: 'Create Sale',
        submitText: 'Create Sale',
      };
    }
  };

  // Create configurations for multiple create buttons (invoice config supports copy)
  const createConfigs = useMemo(
    () => [
      {
        key: 'invoice',
        label: 'Create Clinic Invoice',
        icon: <FileTextOutlined />,
        resource: 'clinic-invoices',
        form: {
          fields: uniqueInvoiceFormFields as any,
          title: 'Create Clinic Invoice',
          submitText: 'Create Clinic Invoice',
          initialValues: copyInvoiceData
            ? {
                ...copyInvoiceData,
                _copyTimestamp: copyInvoiceData.timestamp,
                _copyId: copyInvoiceData.copyId,
                _formInstanceKey: copyInvoiceData.formInstanceKey,
              }
            : {
                patient_id: patientId,
              },
        },
        style: {
          background: '#1890ff',
          borderColor: '#1890ff',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        },
        type: 'primary' as const,
      },
      {
        key: 'sale',
        label: 'Create Medical Invoice',
        icon: <ShoppingCartOutlined />,
        resource: 'sales',
        form: {
          fields: salesFormFields(),
          title: 'Create Medical Invoice',
          submitText: 'Create Medical Invoice',
          initialValues: {
            patient_id: patientId,
            customer_name:
              patient?.name ||
              patient?.full_name ||
              `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() ||
              '',
            customer_phone: patient?.mobile_no
              ? patient.mobile_no.substring(2)
              : '',
          },
        },
        style: {
          background: '#52c41a',
          borderColor: '#52c41a',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        },
        type: 'primary' as const,
      },
      {
        key: 'consultation-invoice',
        label: 'Consultation Invoice',
        icon: <CustomerServiceOutlined />,
        resource: 'clinic-invoices',
        form: {
          fields: consultationInvoiceFormFields as any,
          title: 'Create Consultation Invoice',
          submitText: 'Create Invoice',
          initialValues: {
            patient_id: patientId,
          },
        },
        style: {
          background: '#722ed1',
          borderColor: '#722ed1',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        },
        type: 'primary' as const,
      },
    ],
    [
      uniqueInvoiceFormFields,
      consultationInvoiceFormFields,
      copyInvoiceData,
      patientId,
      patient?.name,
      patient?.full_name,
      patient?.first_name,
      patient?.last_name,
      patient?.mobile_no,
    ]
  );

  // Dynamic form configuration
  const dynamicCreateForm = {
    getForm: (resource: string) => {
      if (resource === 'clinic-invoices') {
        return {
          fields: invoiceFormFields,
          title: 'Create Invoice',
          submitText: 'Create Invoice',
          initialValues: {
            patient_id: patientId,
          },
        };
      } else {
        return {
          fields: salesFormFields(),
          title: 'Create Sale',
          submitText: 'Create Sale',
          initialValues: {
            patient_id: patientId, // Just pass the ID, SearchSelectField will fetch the data
            customer_name:
              patient?.name ||
              patient?.full_name ||
              `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() ||
              '',
            customer_phone: patient?.mobile_no
              ? patient.mobile_no.substring(2)
              : '',
          },
        };
      }
    },
    getResource: (key: string) => {
      return key === 'invoice' ? 'clinic-invoices' : 'sales';
    },
  };

  const dynamicEditForm = {
    getForm: (record: any) => {
      if (record?.type === 'sale') {
        return {
          fields: salesFormFields(record),
          title: 'Edit Sale',
          submitText: 'Save Changes',
          initialValues: {
            patient_id: patientId,
          },
        };
      } else {
        return {
          fields: invoiceFormFields,
          title: 'Edit Invoice',
          submitText: 'Save Changes',
          initialValues: {
            patient_id: patientId,
          },
        };
      }
    },
    getResource: (record: any) => {
      return record?.type === 'sale' ? 'sales' : 'clinic-invoices';
    },
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Main SmartDataTable */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isDataLoading ||
        invoiceFormFields.length === 0 ||
        consultationInvoiceFormFields.length === 0 ||
        salesFormFields.length === 0 ||
        invoiceColumns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip="Loading data and form fields..." />
          </div>
        ) : (
          <SmartDataTable
            api={{
              resource: currentResource, // Use currentResource to switch between invoices and sales
              defaultParams: {
                filters: [
                  {
                    field: 'patient_id',
                    operator: 'eq',
                    value: patientId,
                  },
                ],
              },
            }}
            title={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: '18px', fontWeight: 500 }}>
                  Invoices
                </span>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Tag
                    color="blue"
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      padding: '3px 6px',
                    }}
                  >
                    Invoice Due (₹{totals.totalInvoiceDueAmount.toFixed(2)})
                  </Tag>{' '}
                  <Tag
                    color="green"
                    style={{
                      fontSize: '14px',
                      padding: '3px 6px',
                      fontWeight: 500,
                    }}
                  >
                    Sale Due (₹{totals.totalSaleDueAmount.toFixed(2)})
                  </Tag>
                  <Tag
                    color="orange"
                    style={{
                      fontSize: '14px',
                      padding: '3px 6px',
                      fontWeight: 500,
                    }}
                  >
                    Total Due (₹{totals.totalDueAmount.toFixed(2)})
                  </Tag>
                  <Tag
                    color="red"
                    style={{
                      fontSize: '14px',
                      padding: '3px 6px',
                      fontWeight: 500,
                    }}
                  >
                    Clinic Due (₹{totals.totalClinicDueAmount.toFixed(2)})
                  </Tag>
                </div>
              </div>
            }
            columns={invoiceColumns}
            search={{
              enabled: false,
              fields: [],
            }}
            createConfigs={createConfigs as any}
            dynamicEditForm={dynamicEditForm}
            formDisplayMode="drawer"
            drawerWidth={1200}
            modalWidth={1000}
            showCreate={true}
            showEdit={true}
            showDelete={true}
            showView={false}
            showSizeChanger={false}
            showQuickJumper={false}
            showExport={false}
            showColumnVisibility={true}
            tableProps={{
              scroll: {
                x: 'max-content',
                y: 'calc(100vh - 375px)',
              },
              dataSource: combinedData, // Use the combined data directly
              pagination: false, // Disable pagination to show all data
              // summary: () => (
              //   <Table.Summary.Row
              //     style={{
              //       position: 'sticky',
              //       bottom: 0,
              //       background: '#fafafa',
              //       borderTop: '2px solid #d9d9d9',
              //       fontWeight: 'bold',
              //     }}
              //   >
              //     {/* Column 0: ID */}
              //     <Table.Summary.Cell index={0}>
              //       <div
              //         style={{
              //           textAlign: 'center',
              //           fontWeight: 'bold',
              //           fontSize: '14px',
              //         }}
              //       >
              //         <strong>TOTALS:</strong>
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 1: Details */}
              //     <Table.Summary.Cell index={1}>
              //       <div style={{ textAlign: 'center' }}>
              //         {combinedData.length} Records
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 2: Treatment Details */}
              //     <Table.Summary.Cell index={2}>
              //       <div style={{ textAlign: 'center' }}></div>
              //     </Table.Summary.Cell>

              //     {/* Column 3: Financial Summary */}
              //     <Table.Summary.Cell index={3}>
              //       <div
              //         style={{
              //           textAlign: 'right',
              //           fontWeight: 'bold',
              //           color: '#1890ff',
              //         }}
              //       >
              //         Total Amount: ₹{totals.totalAmount.toFixed(2)}
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 4: Status */}
              //     <Table.Summary.Cell index={4}>
              //       <div
              //         style={{
              //           textAlign: 'center',
              //           fontWeight: 'bold',
              //           color: '#ff4d4f',
              //         }}
              //       >
              //         Total Due: ₹{totals.totalDueAmount.toFixed(2)}
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 5: Notes/Remark */}
              //     <Table.Summary.Cell index={5}>
              //       <div
              //         style={{
              //           textAlign: 'center',
              //           fontSize: '12px',
              //           color: '#8c8c8c',
              //         }}
              //       >
              //         Medical Due: ₹{totals.totalSaleDueAmount.toFixed(2)}
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 6: Updated Date */}
              //     <Table.Summary.Cell index={6}>
              //       <div
              //         style={{
              //           textAlign: 'center',
              //           fontSize: '12px',
              //           color: '#8c8c8c',
              //         }}
              //       >
              //         Clinic Due: ₹{totals.totalInvoiceDueAmount.toFixed(2)}
              //       </div>
              //     </Table.Summary.Cell>

              //     {/* Column 7: User & Timestamp */}
              //     <Table.Summary.Cell index={7}>
              //       <div style={{ textAlign: 'center' }}></div>
              //     </Table.Summary.Cell>
              //   </Table.Summary.Row>
              // ),
            }}
            rowKey="id"
            isFilter={false}
            customActions={customActions}
            autoOpenCreate={autoOpenCreate}
            autoOpenCreateKey={copyInvoiceData ? 'invoice' : undefined}
            onCreateSuccess={handleCreateSuccess}
            onCreateDrawerClose={handleCreateDrawerClose}
            onEditSuccess={() => {
              invalidate({
                resource: 'clinic-invoices',
                invalidates: ['list'],
              });
              invalidate({
                resource: 'sales',
                invalidates: ['list'],
              });
            }}
            onDeleteSuccess={() => {
              invalidate({
                resource: 'clinic-invoices',
                invalidates: ['list'],
              });
              invalidate({
                resource: 'sales',
                invalidates: ['list'],
              });
            }}
            onEditClick={() => {
              // This function is not directly used in the new SmartDataTable
              // as the edit functionality is handled by dynamicEditForm.
              // Keeping it here for now, but it might be removed if not needed.
            }}
          />
        )}
      </div>

      {/* Print Component */}
      {selectedRecordForPrint && (
        <div className="print-content" style={{ display: 'none' }}>
          {selectedRecordForPrint.type === 'invoice' ? (
            <InvoicePrintComponent
              ref={printRef}
              record={selectedRecordForPrint}
              patient={patient}
            />
          ) : (
            <SalesPrintComponent
              ref={printRef}
              record={selectedRecordForPrint}
            />
          )}
        </div>
      )}

      {/* Payment Modal */}
      <PaymentFormModal
        visible={paymentModalVisible}
        onClose={() => {
          setPaymentModalVisible(false);
          setPaymentInitialValues({});
        }}
        onSuccess={() => {
          invalidate({
            resource: 'clinic-invoices',
            invalidates: ['list'],
          });
          invalidate({
            resource: 'sales',
            invalidates: ['list'],
          });
          invalidate({
            resource: 'payments',
            invalidates: ['list'],
          });
          setPaymentModalVisible(false);
          setPaymentInitialValues({});
        }}
        customHeader={{
          message: 'Please take google review from patient',
          type: 'info',
          showIcon: true,
        }}
        initialValues={paymentInitialValues}
      />

    </div>
  );
};
